import React, { useEffect, useState, useRef } from 'react';
import { X, FileSpreadsheet, CheckCircle, Loader2 } from 'lucide-react';

/** segundos → "Xs" (si <60) o "Xm Ys" (transcurrido/restante del export). */
function formatSeconds(sec: number): string {
  const total = Math.max(0, Math.floor(sec));
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}m ${s}s`;
}

interface ProgressToastProps {
  isOpen: boolean;
  onClose: () => void;
  progress: number;
  message: string;
  isComplete?: boolean;
  totalRows?: number;
  // Modo "progreso real" (opt-in): cuando true, la barra refleja EXACTAMENTE `progress`
  // (sin suavizado por tiempo) y el ETA viene de `etaSecOverride` (throughput real del server),
  // no de la heurística de ~1000 filas/s. Se usa en el export streaming server-side.
  realProgress?: boolean;
  etaSecOverride?: number | null;
}

const ProgressToast: React.FC<ProgressToastProps> = ({
  isOpen,
  onClose,
  progress,
  message,
  isComplete = false,
  totalRows,
  realProgress = false,
  etaSecOverride = null
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      // Pequeño delay para la animación de entrada
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  // Tracker de tiempo transcurrido mientras no esté completo
  useEffect(() => {
    if (isOpen && !isComplete) {
      if (startTimeRef.current === null) {
        startTimeRef.current = Date.now();
        setElapsedSec(0);
      }
      const interval = setInterval(() => {
        if (startTimeRef.current !== null) {
          setElapsedSec(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }
      }, 1000);
      return () => clearInterval(interval);
    } else if (isComplete || !isOpen) {
      // Reset al completar o cerrar
      startTimeRef.current = null;
    }
  }, [isOpen, isComplete]);

  useEffect(() => {
    // Auto cerrar después de completar
    if (isComplete && progress === 100) {
      const timer = setTimeout(() => {
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isComplete, progress, onClose]);

  if (!isOpen) return null;

  // Estimar tiempo total basado en heurística: ~1000 filas/segundo, mínimo 5s
  const baseEstimate = totalRows && totalRows > 0
    ? Math.max(5, Math.ceil(totalRows / 1000))
    : null;

  // Recalibrar dinámicamente si ya excedimos la estimación base
  const dynamicEstimate = baseEstimate !== null
    ? (elapsedSec >= baseEstimate ? Math.max(baseEstimate, elapsedSec * 1.5) : baseEstimate)
    : null;

  // ETA: en modo progreso real usamos el override del server (throughput medido); si aún no
  // hay dato (null) mostramos "Calculando…". En modo heurístico, la estimación por tiempo.
  const etaSec = realProgress
    ? etaSecOverride
    : (dynamicEstimate !== null ? Math.max(0, Math.ceil(dynamicEstimate - elapsedSec)) : null);

  // Progreso "suave" basado en tiempo cuando el progreso real no avanza (cap 95% hasta completar)
  const timeBasedProgress = dynamicEstimate !== null
    ? Math.min(95, (elapsedSec / dynamicEstimate) * 100)
    : 0;

  // Mostrar el mayor entre el progreso real y el basado en tiempo (nunca retroceder).
  // En modo progreso real NO suavizamos: la barra refleja exactamente el % real del server.
  const displayedProgress = isComplete
    ? 100
    : (realProgress ? progress : Math.max(progress, timeBasedProgress));
  // ¿Mostrar la fila Transcurrido/Restante? En real siempre (tenemos elapsed real).
  const showEtaRow = realProgress || dynamicEstimate !== null;

  return (
    <div className={`fixed bottom-4 right-4 z-50 transition-all duration-300 ${
      isVisible ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
    }`}>
      <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-4 w-[380px]">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            {isComplete ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <Loader2 className="h-5 w-5 text-green-600 animate-spin" />
            )}
            <div>
              <h4 className="font-medium text-gray-900 flex items-center gap-2">
                {isComplete ? 'Exportación completada' : 'Exportando a Excel'}
                {!isComplete && <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              </h4>
              <p className="text-sm text-gray-600 mt-1">{message}</p>
              {!isComplete && totalRows !== undefined && totalRows > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {totalRows.toLocaleString('es-AR')} productos
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {!isComplete && (
          <div className="mt-4">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Progreso</span>
              <span>{Math.round(displayedProgress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className="bg-green-600 h-full rounded-full transition-all duration-500 ease-out"
                style={{ width: `${displayedProgress}%` }}
              />
            </div>
            {showEtaRow && (
              <div className="flex justify-between text-xs text-gray-500 mt-1 tabular-nums">
                <span>Transcurrido: {formatSeconds(elapsedSec)}</span>
                <span>Restante: {etaSec !== null && etaSec !== undefined ? formatSeconds(etaSec) : 'Calculando…'}</span>
              </div>
            )}
            {!showEtaRow && elapsedSec > 0 && (
              <div className="text-xs text-gray-500 mt-1 tabular-nums">
                Transcurrido: {formatSeconds(elapsedSec)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressToast;
